import type { Organization } from "@/api/organizations";
import { useAppContext } from "@/providers/app-context";

/**
 * If the acting org (a) has white-label permission from CF, (b) has flipped
 * the platform-brand switch in their org settings, and (c) uploaded a logo,
 * return that logo URL. Otherwise null (caller falls back to site branding).
 */
export function orgBrandOverride(
  org:
    | Pick<
        Organization,
        "logo_url" | "whitelabel_enabled" | "use_logo_as_platform_brand"
      >
    | null
    | undefined,
): string | null {
  if (!org?.whitelabel_enabled) return null;
  if (!org.use_logo_as_platform_brand) return null;
  return org.logo_url ?? null;
}

/**
 * The product name to show users. "ForgeIQ" by default; when the acting org
 * has the full whitelabel override active, returns the org's chosen
 * `product_name` (falling back to "Platform" if blank). Backend-wire
 * identifiers (e.g. `user.first_name === "ForgeIQ"`) are NOT covered by this
 * hook — keep those comparisons as-is.
 */
export function useProductName(): string {
  const { actingOrganization } = useAppContext();
  if (!orgBrandOverride(actingOrganization)) return "ForgeIQ";
  return actingOrganization?.product_name?.trim() || "Platform";
}
